import * as Popover from '@radix-ui/react-popover'
import { Check, ChevronDown, Search } from 'lucide-react'
import { type KeyboardEvent, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

export interface SearchSelectOption {
  value: string
  label: string
}

interface SearchSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SearchSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  className?: string
  disabled?: boolean
  id?: string
  ariaLabelledby?: string
}

export function SearchSelect({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder = 'Zoeken',
  emptyLabel = 'Niets gevonden',
  className,
  disabled,
  id,
  ariaLabelledby,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false)
  const [zoek, setZoek] = useState('')
  const [actief, setActief] = useState(0)

  const gekozen = options.find((optie) => optie.value === value) ?? null
  const gefilterd = useMemo(() => {
    const term = zoek.trim().toLowerCase()
    if (!term) return options
    return options.filter((optie) => optie.label.toLowerCase().includes(term))
  }, [options, zoek])

  function kies(volgende: string) {
    onValueChange(volgende)
    setZoek('')
    setActief(0)
    setOpen(false)
  }

  function opToets(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActief((huidig) => Math.min(huidig + 1, gefilterd.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActief((huidig) => Math.max(huidig - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const optie = gefilterd[actief]
      if (optie) kies(optie.value)
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(volgende) => {
        setOpen(volgende)
        if (!volgende) {
          setZoek('')
          setActief(0)
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-labelledby={ariaLabelledby}
          disabled={disabled}
          className={cn(
            'inline-flex h-9 items-center justify-between gap-2 rounded-lg border border-hairline bg-surface px-3 text-sm',
            'focus:border-primair-500/50 focus:outline-hidden focus:ring-2 focus:ring-primair-500/25',
            'disabled:cursor-not-allowed disabled:opacity-60',
            gekozen ? 'text-ink' : 'text-ink-faint',
            className,
          )}
        >
          <span className="truncate">{gekozen ? gekozen.label : placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-faint" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-(--radix-popover-trigger-width) min-w-56 overflow-hidden rounded-card bg-surface shadow-pop animate-fade-in"
        >
          <div className="flex items-center gap-2 border-b border-hairline px-3">
            <Search className="h-4 w-4 shrink-0 text-ink-faint" />
            <input
              autoFocus
              value={zoek}
              onChange={(event) => {
                setZoek(event.target.value)
                setActief(0)
              }}
              onKeyDown={opToets}
              placeholder={searchPlaceholder}
              className="h-10 w-full bg-transparent text-sm text-ink placeholder:text-ink-faint focus:outline-hidden"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1.5">
            {gefilterd.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-ink-faint">{emptyLabel}</p>
            ) : (
              gefilterd.map((optie, index) => (
                <button
                  type="button"
                  key={optie.value}
                  onClick={() => kies(optie.value)}
                  onMouseEnter={() => setActief(index)}
                  className={cn(
                    'relative flex w-full items-center rounded-md px-2.5 py-2 pr-7 text-left text-sm text-ink',
                    index === actief && 'bg-primair-600/10 text-primair-700',
                  )}
                >
                  <span className="truncate">{optie.label}</span>
                  {optie.value === value ? (
                    <Check className="absolute right-2 h-4 w-4 text-primair-600" />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
